import React from 'react';
import PropTypes from 'prop-types';
import { Avatar, Tooltip } from 'antd';
import {
  Building,
  CalendarBlank,
  CaretDoubleLeft,
  CaretDoubleRight,
  Clock,
  Copy,
  FileText,
  Microphone,
  Phone,
  PhoneX,
  Timer,
  X,
} from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import COLORS from '../../../util/constants/colors';
import CallTranscript from '../CallTranscript';
import VoiceRecording from '../VoiceRecording';
import CallQualityFeedback from '../CallQualityFeedback';
import {
  copyValue,
  formatDOB,
  formatDate,
  formatDuration,
  formatName,
  formatPhoneNumber,
  formatTime,
  getInitials,
} from '../helpers';
import { formatDisconnectionReason } from '../disconnectionReasonLabels';
import './voiceCallContext.scss';

export const getDepartmentName = (departmentId, departments = []) => {
  if (!departmentId) return 'No department';

  const department = departments.find((dept) => dept.slug === departmentId);
  return department?.name || 'Unknown department';
};

const sectionClass = (baseClassName, variant) => (
  `voice-call-context-${baseClassName} voice-call-context-${baseClassName}-${variant}`
);

// `started_at` before `created_at` in the fallback chain: both Voice read paths
// emit an identical `started_at`, whereas `created_at` means the call's END time
// on the Firestore path and the row's insert time on the SQL one. Falling back
// to it first would show a different instant depending on which path served the
// request. See CallLogs::SqlIndexQuery#page_of.
const timestampValue = (callLog, timestampField) => (
  callLog?.[timestampField] || callLog?.started_at || callLog?.created_at
);

const ContextMetaCard = ({ icon, label, value }) => (
  <div className="voice-call-context-meta-card">
    <div className="voice-call-context-meta-card-title">
      {icon}
      <Typography
        variant="text"
        size="small"
        className="voice-call-context-meta-card-label"
        color={COLORS.TEXT_SECONDARY_LITE}
      >
        {label}
      </Typography>
    </div>
    <Typography variant="text" size="small" className="voice-call-context-meta-card-value">
      {value || 'N/A'}
    </Typography>
  </div>
);

const HeaderButton = ({
  label,
  icon,
  onClick,
  disabled,
}) => (
  <Tooltip title={label} placement="bottom">
    <Button
      aria-label={label}
      className="icon-btn tertiary medium"
      icon={icon}
      onClick={onClick}
      disabled={disabled}
    />
  </Tooltip>
);

export const VoiceCallContextTitle = ({
  callLog,
  variant,
  timestampField,
  includeDuration,
  showMetaCards,
  showDepartment,
  departments,
  onNavigatePrevious,
  onNavigateNext,
  onClose,
  canNavigatePrevious,
  canNavigateNext,
  navigationLoading,
  previousLabel,
  nextLabel,
  closeLabel,
}) => {
  return (
    <>
      <div className={sectionClass('header', variant)}>
        <div className="voice-call-context-header-title">
          <Avatar size={variant === 'follow-up' ? 44 : 56} className="voice-call-context-avatar">
            {getInitials(callLog?.caller_name)}
          </Avatar>
          <div className="voice-call-context-caller-info">
            <Typography variant="text" size="large" bold className="voice-call-context-caller-name">
              {formatName(callLog?.caller_name) || 'Unknown Caller'}
            </Typography>
            <Typography variant="text" size="small" className="voice-call-context-caller-dob" color={COLORS.TEXT_SECONDARY_LITE}>
              DOB:
              {' '}
              {formatDOB(callLog?.caller_dob) || '--/--/----'}
            </Typography>
          </div>
        </div>
        <div className="voice-call-context-header-actions">
          {onNavigatePrevious && (
            <HeaderButton
              label={previousLabel}
              icon={<CaretDoubleLeft weight="bold" size={20} />}
              onClick={onNavigatePrevious}
              disabled={!canNavigatePrevious || navigationLoading}
            />
          )}
          {onNavigateNext && (
            <HeaderButton
              label={nextLabel}
              icon={<CaretDoubleRight weight="bold" size={20} />}
              onClick={onNavigateNext}
              disabled={!canNavigateNext || navigationLoading}
            />
          )}
          <HeaderButton
            label={closeLabel}
            icon={<X weight="bold" size={20} />}
            onClick={onClose}
          />
        </div>
      </div>
      {showMetaCards && (
        <VoiceCallMetaGrid
          callLog={callLog}
          variant={variant}
          timestampField={timestampField}
          includeDuration={includeDuration}
          showDepartment={showDepartment}
          departments={departments}
        />
      )}
    </>
  );
};

export const VoiceCallMetaGrid = ({
  callLog,
  variant,
  timestampField,
  includeDuration,
  showDepartment,
  departments,
}) => {
  const timestamp = timestampValue(callLog, timestampField);
  const departmentName = getDepartmentName(callLog?.department_id, departments);
  const showDepartmentRow = showDepartment && !!callLog?.department_id;

  return (
    <div className={sectionClass('meta-grid', variant)}>
      <ContextMetaCard icon={<Phone size={16} />} label="Phone" value={formatPhoneNumber(callLog?.caller_number)} />
      <ContextMetaCard icon={<CalendarBlank size={16} />} label="Date" value={formatDate(timestamp)} />
      <ContextMetaCard icon={<Clock size={16} />} label="Time" value={formatTime(timestamp)} />
      {includeDuration && <ContextMetaCard icon={<Timer size={16} />} label="Duration" value={formatDuration(callLog?.duration_seconds)} />}
      <ContextMetaCard icon={<PhoneX size={16} />} label="Status" value={formatDisconnectionReason(callLog)} />
      {showDepartmentRow && (
        <ContextMetaCard icon={<Building size={16} />} label="Department" value={departmentName} />
      )}
    </div>
  );
};

export const VoiceCallSummary = ({ callLog, variant }) => (
  <section className={sectionClass('summary-section', variant)}>
    <div className="voice-call-context-section-header">
      <FileText size={variant === 'follow-up' ? 18 : 24} weight="fill" />
      <Typography variant="text" size="small" className="voice-call-context-section-title" bold>
        Call Summary
      </Typography>
    </div>
    <Typography variant="text" size={variant === 'follow-up' ? 'small' : 'xsmall'} className="voice-call-context-summary-text" color={COLORS.TEXT_SECONDARY_LITE}>
      {callLog?.call_summary || 'No summary available'}
    </Typography>
  </section>
);

export const VoiceCallRecording = ({
  callLog,
  callId,
  isOutbound,
  variant,
  recordingAvailable,
  showFeedback,
}) => (
  <section className={sectionClass('recording-section', variant)}>
    <div className="voice-call-context-section-header">
      <Microphone size={variant === 'follow-up' ? 18 : 24} weight="fill" />
      <Typography variant="text" size="small" className="voice-call-context-section-title" bold>
        Call Recording
      </Typography>
    </div>
    <div className="voice-call-context-recording-body">
      {recordingAvailable === false ? (
        <Typography variant="text" size="small" color={COLORS.TEXT_SECONDARY_LITE}>
          No recording available.
        </Typography>
      ) : (
        <VoiceRecording callId={callId} isOutbound={isOutbound} />
      )}
      {showFeedback && <CallQualityFeedback callSid={callLog?.call_sid} isOutbound={isOutbound} />}
    </div>
  </section>
);

export const VoiceCallTranscript = ({
  callLog,
  transcript,
  defaultExpanded,
  variant,
}) => (
  <section className={sectionClass('transcript-section', variant)}>
    <CallTranscript
      transcript={transcript}
      userName={formatName(callLog?.caller_name) || 'User'}
      defaultExpanded={defaultExpanded}
    />
  </section>
);

export const VoiceCallSid = ({ callSid }) => {
  if (!callSid) return null;

  return (
    <div className="voice-call-context-sid-section">
      <Typography style={{ fontStyle: 'italic' }} variant="text" size="xsmall" color={COLORS.TEXT_SECONDARY_LITE}>
        SID:
        {' '}
        {callSid}
      </Typography>
      <Tooltip title="Copy SID" placement="bottom">
        <Button
          className="icon-btn tertiary xsmall"
          icon={<Copy weight="bold" size={20} />}
          onClick={() => copyValue(callSid, 'Call SID copied to clipboard', 'Failed to copy Call SID')}
        />
      </Tooltip>
    </div>
  );
};

ContextMetaCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ContextMetaCard.defaultProps = {
  value: null,
};

HeaderButton.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

HeaderButton.defaultProps = {
  disabled: false,
};

VoiceCallContextTitle.propTypes = {
  callLog: PropTypes.object,
  variant: PropTypes.oneOf(['inbound', 'follow-up']),
  timestampField: PropTypes.string,
  includeDuration: PropTypes.bool,
  showMetaCards: PropTypes.bool,
  showDepartment: PropTypes.bool,
  departments: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  })),
  onNavigatePrevious: PropTypes.func,
  onNavigateNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  canNavigatePrevious: PropTypes.bool,
  canNavigateNext: PropTypes.bool,
  navigationLoading: PropTypes.bool,
  previousLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  closeLabel: PropTypes.string,
};

VoiceCallContextTitle.defaultProps = {
  callLog: null,
  variant: 'inbound',
  timestampField: 'started_at',
  includeDuration: true,
  showMetaCards: true,
  showDepartment: false,
  departments: [],
  onNavigatePrevious: null,
  onNavigateNext: null,
  canNavigatePrevious: false,
  canNavigateNext: false,
  navigationLoading: false,
  previousLabel: 'Previous Call',
  nextLabel: 'Next Call',
  closeLabel: 'Close',
};

VoiceCallMetaGrid.propTypes = {
  callLog: PropTypes.object,
  variant: PropTypes.oneOf(['inbound', 'follow-up']),
  timestampField: PropTypes.string,
  includeDuration: PropTypes.bool,
  showDepartment: PropTypes.bool,
  departments: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
  })),
};

VoiceCallMetaGrid.defaultProps = {
  callLog: null,
  variant: 'inbound',
  timestampField: 'started_at',
  includeDuration: true,
  showDepartment: false,
  departments: [],
};

VoiceCallSummary.propTypes = {
  callLog: PropTypes.object,
  variant: PropTypes.oneOf(['inbound', 'follow-up']),
};

VoiceCallSummary.defaultProps = {
  callLog: null,
  variant: 'inbound',
};

VoiceCallRecording.propTypes = {
  callLog: PropTypes.object,
  callId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOutbound: PropTypes.bool,
  variant: PropTypes.oneOf(['inbound', 'follow-up']),
  recordingAvailable: PropTypes.bool,
  showFeedback: PropTypes.bool,
};

VoiceCallRecording.defaultProps = {
  callLog: null,
  callId: null,
  isOutbound: false,
  variant: 'inbound',
  recordingAvailable: undefined,
  showFeedback: true,
};

VoiceCallTranscript.propTypes = {
  callLog: PropTypes.object,
  transcript: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  variant: PropTypes.oneOf(['inbound', 'follow-up']),
};

VoiceCallTranscript.defaultProps = {
  callLog: null,
  transcript: null,
  defaultExpanded: true,
  variant: 'inbound',
};

VoiceCallSid.propTypes = {
  callSid: PropTypes.string,
};

VoiceCallSid.defaultProps = {
  callSid: null,
};
